/* =============================================================================
   Nashua Nutrition - IPA Protocol Calculator Logic
   Location: assets/nn-calculator-ipa.js
   Version: 2.3.0
   Last Updated: 2026-03-16
   v2.3.0: Added sugar alcohols to net carbs formula (Total Carbs - Fiber - Sugar Alcohols)

   CHANGES FROM v1 (FINAL_2026_IPA_Calculator_Code.html):
   1. FIX: Decimal gap - changed nc <= 8 to nc < 9
   2. CHANGE: Unrestricted calorie ceiling raised from 150 to 160
   3. CHANGE: Protein upper bound of 23g removed (no protocol basis)
   4. KEEP: 17g buffer zone retained (IPA community practice)
   5. KEEP: Aspartame-only hard fail
   6. EXTRACTED: All inline styles removed - now in nn-calculators.css
   7. FIX (v2.0.1): Incomplete message lists only missing fields dynamically
   8. CLEANUP (v2.0.1): No legacy bootstrap/nn-btn refs anywhere

   PROTOCOL SOURCE: nn_protocol_phase_mapping_v3.md
   Thresholds:
     Unrestricted:     cal <= 160, net carbs < 9, protein >= 12, fat < 30% cal
     Restricted:       cal 161-200 OR net carbs 9-16
     Very Restricted:  net carbs = 17 (1-2 per week)
     Not Protocol:     cal > 200 OR net carbs >= 18 OR protein < 12 OR fat >= 30%
   ============================================================================= */

(function () {
  "use strict";

  var calculators = document.querySelectorAll("[data-nn-calculator='ipa']");
  if (!calculators.length) return;

  calculators.forEach(function (root) {
    var sid = root.getAttribute("data-nn-sid");
    if (!sid) return;
    init(root, sid);
  });

  function init(root, sid) {

    var hardFail = ["aspartame"];

    var softFlags = [
      "high fructose corn syrup",
      "corn syrup",
      "sugar",
      "maltodextrin",
      "polydextrose"
    ];

    var softExplain = {
      "high fructose corn syrup": "Added syrup can increase sugar load.",
      "corn syrup": "Added syrup can increase sugar load.",
      "sugar": "Added sugar may slow your progress.",
      "maltodextrin": "Fast carbs can raise net carbs.",
      "polydextrose": "May affect digestion for some people."
    };

    function toNum(v) {
      if (v === null || v === undefined) return null;
      var s = String(v).trim().replace(/,/g, "");
      if (!s) return null;
      var x = parseFloat(s);
      return isNaN(x) ? null : x;
    }

    function netCarbs(p) {
      if (p.carbs_g == null || p.fiber_g == null) return null;
      var sa = p.sugar_alcohols_g || 0;
      var x = p.carbs_g - p.fiber_g - sa;
      return x < 0 ? 0 : x;
    }

    function scanIngredients(text) {
      var provided = !!String(text || "").trim();
      if (!provided) return { provided: false, softFlagTerms: [] };
      var lower = String(text).toLowerCase();
      var hard = null;
      for (var i = 0; i < hardFail.length; i++) {
        if (lower.indexOf(hardFail[i]) !== -1) { hard = hardFail[i]; break; }
      }
      var flags = [];
      for (var j = 0; j < softFlags.length; j++) {
        if (lower.indexOf(softFlags[j]) !== -1) flags.push(softFlags[j]);
      }
      return { provided: true, hardFailTerm: hard, softFlagTerms: flags };
    }

    function anyMissing(p) {
      return (
        p.total_cal == null ||
        p.fat_g == null ||
        p.protein_g == null ||
        p.carbs_g == null ||
        p.fiber_g == null
      );
    }

    function missingFieldsMsg(p) {
      var missing = [];
      if (p.total_cal == null) missing.push("calories");
      if (p.fat_g == null) missing.push("fat");
      if (p.protein_g == null) missing.push("protein");
      if (p.carbs_g == null) missing.push("total carbs");
      if (p.fiber_g == null) missing.push("fiber");
      if (missing.length === 0) return "";
      if (missing.length === 1) return "Add " + missing[0] + ".";
      var last = missing.pop();
      return "Add " + missing.join(", ") + ", and " + last + ".";
    }

    /* ------- Phase classification --------------------------------------- */

    function classifyPhase1(p) {
      var nc = netCarbs(p);
      var ing = scanIngredients(p.ingredient_text);

      if (ing.hardFailTerm) {
        return {
          phase: 1,
          classification: "Non-Protocol",
          reason: "Aspartame does not fit the plan.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (anyMissing(p) || nc == null) {
        return {
          phase: 1,
          classification: "Incomplete",
          reason: missingFieldsMsg(p),
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      var cal = p.total_cal;
      var fat = p.fat_g;
      var protein = p.protein_g;

      if (protein < 12) {
        return {
          phase: 1,
          classification: "Not Protocol",
          reason: "Protein is below 12g.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      var fatCal = fat * 9;
      var maxFatCal = cal * 0.30;
      if (fatCal > maxFatCal) {
        return {
          phase: 1,
          classification: "Not Protocol",
          reason: "Fat calories are over 30% of total calories.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (cal > 200) {
        return {
          phase: 1,
          classification: "Not Protocol",
          reason: "Calories are over 200.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (nc >= 18) {
        return {
          phase: 1,
          classification: "Not Protocol",
          reason: "Net carbs are 18 or more.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (cal <= 160 && nc < 9) {
        return {
          phase: 1,
          classification: "Compliant",
          reason: "Fits Phase 1 limits for calories and net carbs.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (nc === 17) {
        return {
          phase: 1,
          classification: "Restricted",
          guidance: "Have 1-2 per week.",
          reason: "Net carbs are 17g.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if ((cal >= 161 && cal <= 200) || (nc >= 9 && nc <= 16)) {
        return {
          phase: 1,
          classification: "Restricted",
          guidance: "Limit to one per day.",
          reason: "Restricted by calories and/or net carbs.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      return {
        phase: 1,
        classification: "Not Protocol",
        reason: "Does not fit Phase 1 limits.",
        netCarbs: nc,
        ingredientScan: ing
      };
    }

    function classifyPhase2(p) {
      var nc = netCarbs(p);
      var ing = scanIngredients(p.ingredient_text);

      if (ing.hardFailTerm) {
        return {
          phase: 2,
          classification: "Non-Protocol",
          reason: "Aspartame does not fit the plan.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      var p1 = classifyPhase1(p);

      if (p1.classification === "Incomplete") {
        return {
          phase: 2,
          classification: "Incomplete",
          reason: p1.reason,
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (p1.classification === "Compliant") {
        return {
          phase: 2,
          classification: "Phase 2 Compliant",
          reason: "This item is fully compliant in Phase 2.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (p1.classification === "Restricted") {
        return {
          phase: 2,
          classification: "Phase 2 Advisory",
          guidance: "Use it in moderation.",
          reason: "This item is a Phase 2 warning.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (nc != null && nc <= 25) {
        return {
          phase: 2,
          classification: "Phase 2 Advisory",
          guidance: "Use it in moderation.",
          reason: "May fit Phase 2 for some people.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      return {
        phase: 2,
        classification: "Not Protocol",
        reason: "Does not fit typical Phase 2 patterns.",
        netCarbs: nc,
        ingredientScan: ing
      };
    }

    function classifyPhase3(p) {
      var nc = netCarbs(p);
      var ing = scanIngredients(p.ingredient_text);

      if (ing.hardFailTerm) {
        return {
          phase: 3,
          classification: "Non-Protocol",
          reason: "Aspartame does not fit the plan.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      var p1 = classifyPhase1(p);
      if (p1.classification === "Incomplete") {
        return {
          phase: 3,
          classification: "Incomplete",
          reason: p1.reason,
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      if (p1.classification === "Compliant") {
        return {
          phase: 3,
          classification: "Phase 3 Compliant",
          reason: "Fits long-term maintenance patterns.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      var p2 = classifyPhase2(p);
      if (
        p2.classification === "Phase 2 Compliant" ||
        p2.classification === "Phase 2 Advisory"
      ) {
        return {
          phase: 3,
          classification: "Phase 3 Compliant",
          reason: "Fits Phase 3 when balanced.",
          netCarbs: nc,
          ingredientScan: ing
        };
      }

      return {
        phase: 3,
        classification: "Phase 3 Advisory",
        guidance: "Balance it with lean protein and veggies.",
        reason: "Use care in Phase 3.",
        netCarbs: nc,
        ingredientScan: ing
      };
    }

    function classifyAll(p) {
      return {
        phase1: classifyPhase1(p),
        phase2: classifyPhase2(p),
        phase3: classifyPhase3(p)
      };
    }

    /* ------- Display helpers -------------------------------------------- */

    function toneFor(r) {
      if (
        r.classification === "Compliant" ||
        r.classification === "Phase 2 Compliant" ||
        r.classification === "Phase 3 Compliant"
      ) return "good";
      if (
        r.classification === "Restricted" ||
        r.classification === "Phase 2 Advisory" ||
        r.classification === "Phase 3 Advisory"
      ) return "warn";
      if (
        r.classification === "Not Protocol" ||
        r.classification === "Non-Protocol"
      ) return "bad";
      return "neutral";
    }

    function titleFor(r) {
      var prefix = "Phase " + r.phase;
      if (
        r.classification === "Compliant" ||
        r.classification === "Phase 2 Compliant" ||
        r.classification === "Phase 3 Compliant"
      ) return prefix + " Compliant";
      if (r.classification === "Restricted") return prefix + " Restricted";
      if (
        r.classification === "Phase 2 Advisory" ||
        r.classification === "Phase 3 Advisory"
      ) return prefix + " Warning";
      if (
        r.classification === "Not Protocol" ||
        r.classification === "Non-Protocol"
      ) return prefix + " Non-compliant";
      return prefix + " Incomplete";
    }

    function statusTextFor(t) {
      if (t === "good") return "Allowed";
      if (t === "warn") return "Warning";
      if (t === "bad") return "Not allowed";
      return "Needs info";
    }

    function bannerHTML(scan) {
      if (!scan || !scan.provided) return "";
      if (scan.hardFailTerm) {
        return '<div class="nn-banner bad"><span class="nn-dot bad"></span><div>Not allowed: aspartame is flagged.</div></div>';
      }
      if (scan.softFlagTerms && scan.softFlagTerms.length) {
        var first = scan.softFlagTerms[0];
        var msg = softExplain[first] || "Check this ingredient for fit.";
        return '<div class="nn-banner warn"><span class="nn-dot warn"></span><div>Warning: ' + msg + '</div></div>';
      }
      return '<div class="nn-banner good"><span class="nn-dot good"></span><div>Ingredients check: no flagged terms found.</div></div>';
    }

    function cardHTML(r) {
      var tone = toneFor(r);
      var title = titleFor(r);
      var guidance = r.guidance
        ? '<div class="nn-tip"><strong>Tip:</strong> ' + r.guidance + "</div>"
        : "";
      var kv =
        typeof r.netCarbs === "number"
          ? '<div class="nn-kv"><span>Net carbs: ' + r.netCarbs.toFixed(1) + 'g</span></div>'
          : "";

      return (
        '<div class="nn-card ' + tone + '">' +
        '<div class="nn-title-row">' +
        '<div class="nn-title ' + tone + '">' + title + '</div>' +
        '<div class="nn-pill ' + tone + '">' + statusTextFor(tone) + '</div>' +
        '</div>' +
        '<div class="nn-why">' + r.reason + '</div>' +
        guidance + kv + bannerHTML(r.ingredientScan) +
        '</div>'
      );
    }

    /* ------- Event wiring ---------------------------------------------- */

    function getProduct() {
      return {
        total_cal: toNum(document.getElementById("nn_cal_" + sid).value),
        fat_g: toNum(document.getElementById("nn_fat_" + sid).value),
        protein_g: toNum(document.getElementById("nn_pro_" + sid).value),
        carbs_g: toNum(document.getElementById("nn_carbs_" + sid).value),
        fiber_g: toNum(document.getElementById("nn_fiber_" + sid).value),
        sugar_alcohols_g: toNum(document.getElementById("nn_sa_" + sid).value) || 0,
        ingredient_text: document.getElementById("nn_ing_" + sid).value || ""
      };
    }

    function run() {
      var all = classifyAll(getProduct());
      var box = document.getElementById("nn-results-" + sid);
      if (!box) return;
      box.style.display = "flex";
      box.innerHTML = cardHTML(all.phase1) + cardHTML(all.phase2) + cardHTML(all.phase3);
    }

    function reset() {
      ["nn_cal_", "nn_fat_", "nn_pro_", "nn_carbs_", "nn_fiber_", "nn_sa_", "nn_ing_"].forEach(
        function (prefix) {
          var el = document.getElementById(prefix + sid);
          if (el) el.value = "";
        }
      );
      var box = document.getElementById("nn-results-" + sid);
      if (box) {
        box.style.display = "none";
        box.innerHTML = "";
      }
    }

    var runBtn = document.getElementById("nn-run-" + sid);
    var resetBtn = document.getElementById("nn-reset-" + sid);
    var form = document.getElementById("nn-form-" + sid);

    if (runBtn) runBtn.addEventListener("click", function(e){ e.preventDefault(); run(); });
    if (resetBtn) resetBtn.addEventListener("click", function(e){ e.preventDefault(); reset(); });

    if (form) {
      form.addEventListener("keydown", function (e) {
        if (e.key === "Enter") {
          e.preventDefault();
          run();
        }
      });
    }
  }
})();
